import { useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../redux/store";
import { useSnackbar } from "notistack";

import { Outlet, Navigate, useLocation } from "react-router";

import { validateUserToken } from "../../redux/slices/loginSlice";
import {
  setActiveTab,
  fetchProjects,
} from "../../redux/slices/projectsSlice";
import ClientHome from "./components/ClientHome";
import ProjectDetailsDrawer from "./components/Projects/components/ProjectDetailsDrawer";
import SetOwnPasswordDialog from "./components/SetOwnPasswordDialog";
import { TABS } from "./personal-cabinet/constants";
import { PageHeader, Tabs } from "../../ui";
import PortalShell from "../../components/PortalShell";

/** Project name + the two client views, in the reference's header style. */
function ClientProjectHeader() {
  const dispatch = useAppDispatch();
  const { projects, selectedProject, activeTab } = useAppSelector(
    (state) => state.projects,
  ) as any;
  const project = selectedProject || projects?.[0];

  return (
    <div style={{ marginBottom: 24 }}>
      <PageHeader
        eyebrow="Project"
        title={project?.name || "Your project"}
        summary={project?.address || project?.billingAddress || undefined}
      />
      <Tabs
        value={String(activeTab ?? TABS.INVOICES)}
        onChange={(value) => dispatch(setActiveTab(Number(value)))}
        items={[
          { value: String(TABS.INVOICES), label: "Invoices" },
          { value: String(TABS.TRANSACTIONS), label: "Transactions" },
          { value: String(TABS.BANK), label: "Bank details" },
        ]}
      />
    </div>
  );
}

export default function Layout() {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { needValidation, userIsValid, user } = useAppSelector(
    (state) => state.login,
  );
  const {
    projects,
    fetchProjectsStatus,
    updateProjectPaymentMethodStatus,
    deleteProjectPaymentMethodStatus,
    updateProjectBillingAddressStatus,
    updateProjectPaymentMethodError,
    deleteProjectPaymentMethodError,
    updateProjectBillingAddressError,
  } = useAppSelector((state) => state.projects) as any;

  const location = useLocation();

  useEffect(() => {
    if (needValidation && !userIsValid) {
      dispatch(validateUserToken());
    }
  }, [dispatch, needValidation, userIsValid]);

  // ── Project operation snackbar notifications ──
  useEffect(() => {
    if (updateProjectPaymentMethodStatus === "succeeded") {
      enqueueSnackbar("Project updated successfully", { variant: "success" });
    }
    if (updateProjectPaymentMethodStatus === "failed") {
      enqueueSnackbar(updateProjectPaymentMethodError, { variant: "error" });
    }
  }, [
    updateProjectPaymentMethodStatus,
    updateProjectPaymentMethodError,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    if (deleteProjectPaymentMethodStatus === "succeeded") {
      enqueueSnackbar("Payment method deleted successfully", {
        variant: "success",
      });
    }
    if (deleteProjectPaymentMethodStatus === "failed") {
      enqueueSnackbar(deleteProjectPaymentMethodError, { variant: "error" });
    }
  }, [
    deleteProjectPaymentMethodStatus,
    deleteProjectPaymentMethodError,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    if (updateProjectBillingAddressStatus === "succeeded") {
      enqueueSnackbar("Billing address updated successfully", {
        variant: "success",
      });
    }
    if (updateProjectBillingAddressStatus === "failed") {
      enqueueSnackbar(updateProjectBillingAddressError, { variant: "error" });
    }
  }, [
    updateProjectBillingAddressStatus,
    updateProjectBillingAddressError,
    enqueueSnackbar,
  ]);

  useEffect(() => {
    // Fetch projects for client/employee users for the project switcher and
    // the client home.
    if (user?.role === "client" || user?.role === "employee") {
      if (
        fetchProjectsStatus === "idle" ||
        (fetchProjectsStatus === "failed" && !projects?.length)
      ) {
        dispatch(fetchProjects());
      }
    }
  }, [dispatch, user?.role, fetchProjectsStatus, projects?.length]);

  if (!needValidation && !userIsValid) {
    return <Navigate to="/login" replace />;
  }

  const isClientHome =
    location.pathname === "/project-home" &&
    (user?.role === "client" || user?.role === "employee");
  const isFinancialPage =
    location.pathname === "/project-home/financial" &&
    (user?.role === "client" || user?.role === "employee");

  // While an admin-set password is still in place there is nothing else to do
  // here: the API refuses everything else, so the rest of the portal is left
  // out rather than filling the screen with refusals behind the dialog.
  if (user?.mustChangePassword) {
    return <SetOwnPasswordDialog />;
  }

  return (
    <PortalShell>
      {isClientHome ? (
        <ClientHome />
      ) : (
        <>
          {isFinancialPage && <ClientProjectHeader />}
          <Outlet />
        </>
      )}

      {/* Project details drawer — opened from the project rows in the nav. */}
      {(user?.role === "client" || user?.role === "employee") && (
        <ProjectDetailsDrawer />
      )}

      {/* An admin-set password gets replaced by one of their own before the
          portal is used: whoever set it knows it. */}
      <SetOwnPasswordDialog />
    </PortalShell>
  );
}
